import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Dialog,
  TextField,
  Chip,
  Button,
  Autocomplete
} from '@mui/material';
import { ArrowBack, MoreVert, Add } from '@mui/icons-material';
import ChatThread from './ChatThread';
import { ChatRoom } from '../../types/chat.types';
import chatApi from '../../utils/chatApi';
import * as userApi from '../../utils/userApi';
import useAuthStore from '../../store/zustand/auth';
import { useChatStore } from '../../store/useChatStore';
import { useSocket } from '../../hooks/useSocket';

interface ChatViewMobileProps {
  companyId: string;
  userId: string;
  userLanguage: string;
}

const getInitials = (name: string): string => {
  if (!name) return '?';
  const words = name
    .replace(/[^a-zA-Z0-9\s&]/g, '')
    .split(/[\s&]+/)
    .filter((word) => word.length > 0);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  if (words.length >= 2) {
    return words
      .slice(0, Math.min(3, words.length))
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + (hash * 31 - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 50%)`;
};

function ChatViewMobile({
  companyId,
  userId,
  userLanguage
}: ChatViewMobileProps): JSX.Element {
  const { joinRoom, leaveRoom } = useSocket({
    companyId,
    enabled: true,
    userId
  });

  const rooms = useChatStore((state) => state.rooms);
  const setRooms = useChatStore((state) => state.setRooms);
  const clearUnread = useChatStore((state) => state.clearUnread);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [preferredLanguage, setPreferredLanguage] =
    useState<string>(userLanguage);
  const updateUserInStore = useAuthStore((state) => state.setUser);
  const userState = useAuthStore((state) => state);

  const [showAllChannels, setShowAllChannels] = useState(false);
  const [showAllChats, setShowAllChats] = useState(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomType, setNewRoomType] = useState<'group' | 'direct'>('group');
  const [selectedParticipants, setSelectedParticipants] = useState<any[]>([]);

  const [createError, setCreateError] = useState('');

  const loadRooms = async () => {
    try {
      const roomData = await chatApi.getRooms();
      setRooms(roomData);
      roomData.forEach((room) => {
        joinRoom(room.id);
      });
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const userData = await chatApi.getUsers();
      setUsers(userData.filter((u: any) => u.active));
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    loadRooms();
    loadUsers();
  }, []);

  const handleLanguageChange = async (event: any) => {
    const newLanguage = event.target.value;
    setPreferredLanguage(newLanguage);
    try {
      await userApi.updateLanguagePreference(newLanguage);
      updateUserInStore({ ...userState, preferredLanguage: newLanguage });
    } catch (error) {
      console.error('Failed to update language preference:', error);
    }
  };

  const handleRoomSelect = (room: ChatRoom) => {
    setSelectedRoom(room);
    clearUnread(room.id);
    chatApi.markRoomAsRead(room.id).catch(() => {});
  };
  const handleBackToList = () => {
    setSelectedRoom(null);
  };
  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setNewRoomName('');
    setNewRoomType('group');
    setSelectedParticipants([]);
  };
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateError('');
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      setCreateError('Please enter a room name');
      return;
    }
    if (selectedParticipants.length === 0) {
      setCreateError('Please select at least one participant');
      return;
    }
    setCreateError('');

    try {
      const participantIds = selectedParticipants.map((p) => p.id);
      await chatApi.createRoom({
        name: newRoomName.trim(),
        participantIds,
        type: newRoomType
      });
    } catch (error) {
      console.warn(
        'Room creation returned error (but room may have been created):',
        error
      );
    } finally {
      await loadRooms();
      handleCloseCreateDialog();
      const roomData = await chatApi.getRooms();
      const newRoom = roomData.find((r) => r.name === newRoomName.trim());
      if (newRoom) setSelectedRoom(newRoom);
    }
  };

  const channelRooms = rooms
    .filter((r) => r.type === 'group' || r.type === 'project')
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return a.name.localeCompare(b.name);
    });

  const directRooms = rooms
    .filter((r) => r.type === 'direct')
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return a.name.localeCompare(b.name);
    });

  const renderRoomItem = (room: ChatRoom, isDirect = false) => {
    let displayName = room.name;
    let initials = getInitials(room.name);

    if (isDirect) {
      const otherUserId = room.participantIds?.find((id) => id !== userId);
      const otherUser = users.find((u) => u.id === otherUserId);
      displayName = otherUser
        ? `${otherUser.firstName} ${otherUser.lastName}`
        : room.name;
      initials = otherUser
        ? `${otherUser.firstName?.[0] || ''}${
            otherUser.lastName?.[0] || ''
          }`.toUpperCase()
        : getInitials(room.name);
    }

    const avatarColor = stringToColor(displayName);

    return (
      <ListItem
        button
        key={room.id}
        onClick={() => handleRoomSelect(room)}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}
      >
        <ListItemAvatar>
          <Badge
            badgeContent={(room.unreadCount ?? 0) > 0 ? room.unreadCount : null}
            color="error"
          >
            <Avatar
              sx={{
                bgcolor: avatarColor,
                fontSize: '0.875rem',
                fontWeight: 600,
                height: 40,
                width: 40
              }}
            >
              {initials}
            </Avatar>
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography
              sx={{
                fontWeight: (room.unreadCount ?? 0) > 0 ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              variant="body1"
            >
              {displayName}
            </Typography>
          }
          secondary={room.lastMessage?.messageText || 'No messages yet'}
        />
      </ListItem>
    );
  };

  if (selectedRoom) {
    return (
      <Box
        sx={{
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          left: 0,
          paddingTop: '80px',
          position: 'fixed',
          right: 0,
          top: 0
        }}
      >
        <Paper
          elevation={1}
          sx={{
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
            display: 'flex',
            flexShrink: 0,
            px: 1,
            py: 1
          }}
        >
          <IconButton
            edge="start"
            onClick={handleBackToList}
            size="small"
            sx={{ mr: 1 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography sx={{ flex: 1, fontSize: '1rem', fontWeight: 500 }}>
            {selectedRoom.name}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 60, mr: 1 }}>
            <Select
              onChange={handleLanguageChange}
              sx={{ '.MuiSelect-select': { py: 0.5 }, fontSize: '0.75rem' }}
              value={preferredLanguage}
            >
              <MenuItem value="en">EN</MenuItem>
              <MenuItem value="es">ES</MenuItem>
              <MenuItem value="pl">PL</MenuItem>
              <MenuItem value="ja">JA</MenuItem>
            </Select>
          </FormControl>
          <IconButton size="small">
            <MoreVert />
          </IconButton>
        </Paper>

        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <ChatThread
            joinRoom={joinRoom}
            key={`${selectedRoom.id}-${preferredLanguage}`}
            leaveRoom={leaveRoom}
            onParticipantsChanged={async () => {
              await loadRooms();
              const updatedRoom = useChatStore
                .getState()
                .rooms.find((r) => r.id === selectedRoom?.id);
              if (updatedRoom) setSelectedRoom(updatedRoom);
            }}
            onRoomDeleted={() => {
              setSelectedRoom(null);
              loadRooms();
            }}
            onRoomRenamed={(roomId, newName) => {
              loadRooms().then(() => {
                setSelectedRoom((prev) =>
                  prev?.id === roomId ? { ...prev, name: newName } : prev
                );
              });
            }}
            room={selectedRoom}
            userId={userId}
            userLanguage={preferredLanguage}
            users={users}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        left: 0,
        paddingTop: '80px',
        position: 'fixed',
        right: 0,
        top: 0
      }}
    >
      <Paper
        elevation={1}
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
          display: 'flex',
          flexShrink: 0,
          px: 2,
          py: 1
        }}
      >
        <Typography sx={{ flex: 1, fontSize: '1rem', fontWeight: 500 }}>
          Chat
        </Typography>
        <FormControl size="small" sx={{ minWidth: 60, mr: 1 }}>
          <Select
            onChange={handleLanguageChange}
            sx={{ '.MuiSelect-select': { py: 0.5 }, fontSize: '0.75rem' }}
            value={preferredLanguage}
          >
            <MenuItem value="en">EN</MenuItem>
            <MenuItem value="es">ES</MenuItem>
            <MenuItem value="pl">PL</MenuItem>
            <MenuItem value="ja">JA</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={handleOpenCreateDialog} size="small">
          <Add />
        </IconButton>
      </Paper>

      <Box sx={{ bgcolor: 'background.default', flex: 1, overflow: 'auto' }}>
        <Box sx={{ py: 0.5 }}>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              px: 2,
              py: 0.5
            }}
            variant="subtitle2"
          >
            CHANNELS
          </Typography>
          <List disablePadding>
            {(showAllChannels ? channelRooms : channelRooms.slice(0, 10)).map(
              (room) => renderRoomItem(room, false)
            )}
          </List>
          {channelRooms.length > 10 && (
            <Button
              fullWidth
              onClick={() => setShowAllChannels(!showAllChannels)}
              size="small"
              sx={{ px: 2, py: 1 }}
            >
              {showAllChannels
                ? 'Show Less'
                : `Show ${channelRooms.length - 10} More`}
            </Button>
          )}
        </Box>

        <Divider />

        <Box sx={{ py: 0.5 }}>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              px: 2,
              py: 0.5
            }}
            variant="subtitle2"
          >
            PRIVATE CHATS
          </Typography>
          <List disablePadding>
            {(showAllChats ? directRooms : directRooms.slice(0, 5)).map(
              (room) => renderRoomItem(room, true)
            )}
          </List>
          {directRooms.length > 5 && (
            <Button
              fullWidth
              onClick={() => setShowAllChats(!showAllChats)}
              size="small"
              sx={{ px: 2, py: 1 }}
            >
              {showAllChats
                ? 'Show Less'
                : `Show ${directRooms.length - 5} More`}
            </Button>
          )}
        </Box>
      </Box>

      <Dialog
        fullScreen
        onClose={handleCloseCreateDialog}
        open={createDialogOpen}
      >
        <Paper
          elevation={1}
          sx={{
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            p: 2
          }}
        >
          <IconButton edge="start" onClick={handleCloseCreateDialog}>
            <ArrowBack />
          </IconButton>
          <Typography sx={{ flex: 1, ml: 2 }} variant="h6">
            Create Room
          </Typography>
          <Button onClick={handleCreateRoom} variant="contained">
            Create
          </Button>
        </Paper>

        <Box sx={{ p: 2 }}>
          <TextField
            autoFocus
            fullWidth
            error={Boolean(createError)}
            helperText={createError || ' '}
            label="Room Name"
            margin="dense"
            onChange={(e) => {
              setNewRoomName(e.target.value);
              setCreateError('');
            }}
            value={newRoomName}
          />

          <FormControl fullWidth margin="dense">
            <Select
              onChange={(e) => setNewRoomType(e.target.value as any)}
              value={newRoomType}
            >
              <MenuItem value="group">Group Channel</MenuItem>
              <MenuItem value="direct">Direct Message</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ mb: 1, mt: 3 }} variant="subtitle2">
            Add Participants:
          </Typography>

          <Autocomplete
            multiple
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  `${option.firstName} ${option.lastName}`
                    .toLowerCase()
                    .includes(inputValue) ||
                  option.email?.toLowerCase().includes(inputValue)
              );
            }}
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, newValue) => {
              setSelectedParticipants(newValue);
            }}
            options={users.filter((user) => user.id !== userId)}
            renderInput={(params) => (
              <TextField
                InputLabelProps={params.InputLabelProps}
                InputProps={params.InputProps}
                disabled={params.disabled}
                fullWidth={params.fullWidth}
                helperText="Type to search and select participants"
                id={params.id}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                inputProps={params.inputProps}
                size={params.size}
              />
            )}
            renderOption={(props, option) => {
              /* eslint-disable react/prop-types */
              const { key: optKey, ...liProps } =
                props as React.HTMLAttributes<HTMLLIElement> & { key: string };
              return (
                <React.Fragment key={optKey}>
                  {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                  <li {...liProps}>
                    <Box>
                      <Typography variant="body2">
                        {option.firstName} {option.lastName}
                      </Typography>
                      {option.email && (
                        <Typography color="text.secondary" variant="caption">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                </React.Fragment>
              );
              /* eslint-enable react/prop-types */
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                return (
                  <Chip
                    key={option.id}
                    label={`${option.firstName} ${option.lastName}`}
                    onDelete={tagProps.onDelete}
                    size="small"
                  />
                );
              })
            }
            value={selectedParticipants}
          />
        </Box>
      </Dialog>
    </Box>
  );
}

export default ChatViewMobile;
